/** EvalConfigPanel — edit an agent's optimization config.
 *
 * The optimization goal + weighted judge dimensions + pass threshold steer both
 * the LLM judge (how it scores) and the improver (what it optimizes toward), and
 * are saved per agent.
 */

import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import type { EvalDimension } from '@/entities/eval';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import type { UseEvalConfigResult } from '../../../features/settings/evaluation/useEvalConfig';

export default function EvalConfigPanel({ state }: { state: UseEvalConfigResult }) {
  const { config, loading, saving, error, save } = state;

  const [goal, setGoal] = useState('');
  const [dimensions, setDimensions] = useState<EvalDimension[]>([]);
  const [threshold, setThreshold] = useState<string>('');

  // Seed the editable form once the saved config loads.
  useEffect(() => {
    if (config) {
      setGoal(config.optimization_goal ?? '');
      setDimensions(config.dimensions);
      setThreshold(config.pass_threshold == null ? '' : String(config.pass_threshold));
    }
  }, [config]);

  const updateDim = (i: number, patch: Partial<EvalDimension>) =>
    setDimensions((ds) => ds.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const removeDim = (i: number) => setDimensions((ds) => ds.filter((_, idx) => idx !== i));
  const addDim = () => setDimensions((ds) => [...ds, { name: '', weight: 1, description: '' }]);

  const onSave = async () => {
    const cleaned = dimensions
      .map((d) => ({ ...d, name: d.name.trim() }))
      .filter((d) => d.name.length > 0);
    if (cleaned.length === 0) {
      toast.error('Add at least one scoring dimension.');
      return;
    }
    const parsed = threshold.trim() === '' ? null : Number(threshold);
    const ok = await save({
      optimization_goal: goal.trim() || null,
      dimensions: cleaned,
      pass_threshold: parsed,
    });
    if (ok) toast.success('Optimization config saved — applies to the next eval run.');
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading optimization config…</p>;
  }

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3"
      aria-label="Optimization config"
    >
      <div>
        <h3 className="text-lg font-semibold text-foreground">Scoring &amp; optimization goal</h3>
        <p className="text-sm text-muted-foreground">
          Steers how the judge scores and what the improver optimizes toward. Saved per agent.
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="eval-goal">Optimization goal</Label>
        <Textarea
          id="eval-goal"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          placeholder="e.g. Always name the exact tool, stay under 120 words, never fabricate a result."
          rows={2}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label>Scoring dimensions (0–100 each; weighted into the overall)</Label>
        {dimensions.map((d, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              aria-label={`Dimension ${i + 1} name`}
              className="w-40"
              value={d.name}
              onChange={(e) => updateDim(i, { name: e.target.value })}
              placeholder="name"
            />
            <Input
              aria-label={`Dimension ${i + 1} weight`}
              type="number"
              min={0}
              step={0.5}
              className="w-20"
              value={d.weight}
              onChange={(e) => updateDim(i, { weight: Number(e.target.value) })}
            />
            <Input
              aria-label={`Dimension ${i + 1} description`}
              className="flex-1"
              value={d.description ?? ''}
              onChange={(e) => updateDim(i, { description: e.target.value })}
              placeholder="what this dimension checks (given to the judge)"
            />
            <Button variant="ghost" size="sm" onClick={() => removeDim(i)} aria-label={`Remove dimension ${i + 1}`}>
              ✕
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" className="self-start" onClick={addDim}>
          Add dimension
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Label htmlFor="eval-threshold">Pass threshold</Label>
        <Input
          id="eval-threshold"
          type="number"
          min={0}
          max={100}
          className="w-24"
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          placeholder="none"
        />
        <span className="text-sm text-muted-foreground">cells at/above this score read as passing</span>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Button className="self-start" onClick={() => void onSave()} disabled={saving}>
        {saving ? 'Saving…' : 'Save config'}
      </Button>
    </section>
  );
}
